import {
  ElementRef,
  Component,
  ViewChild,
  AfterViewChecked,
  forwardRef,
  HostBinding,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseCustomControl } from '../baseCustomControl';
import { iValidatedControl } from '../iValidatedControl';
import { ListItem } from '../../../../../../../../libs/Shared/components/model/listItem';

@Component({
  selector: 'app-auto-fill-input',
  templateUrl: './auto-fill-input.component.html',
  providers: [
    // see https://blog.thoughtram.io/angular/2016/07/27/custom-form-controls-in-angular-2.html
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoFillInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AutoFillInputComponent
  extends BaseCustomControl
  implements ControlValueAccessor, iValidatedControl, AfterViewChecked
{
  @HostBinding('class.formField') formFieldClass = true; // Always applied
  @HostBinding('class.readOnly') get isReadOnly() {
    return this.readonly;
  }

  @ViewChild('ddl') selectElement: ElementRef<HTMLSelectElement> | undefined;

  @Input() validationLocation: string = 'right'; // right | under
  @Input() optionsFunction: Function | undefined;
  @Input() testId: string = '';

  /*
  Implement something like this in the parent component and pass it for the optionsFunction:
  
  public getUsers(param:string):Promise<ListItem[]> {
    if (param) {
      return BasePage.userService.getPaged("", "", param.trim(), 0, 10)
      .then(response => {
        return response;
      })
      .then((page:Page<User>) => {
        return page.records.map((user:User) => (new ListItem({id:user.id, name:user.firstName + " " + user.lastName + " (" + user.emailAddress + ")"})));
      });
    }
    else {
      throw new Error("No parameter provided");
    }
  }

  */

  sQuery: string = '';
  selectedOption: string | undefined = undefined;
  options: any[] = [];

  activeTyping: boolean = false;
  isSelected: boolean = false;
  optionsChanged: boolean = false;

  get value(): string {
    if (this.boundValue) {
      return this.boundValue.trim();
    }
    return '';
  }

  public validate(): boolean {
    this.isValid = true;
    this.validationMsg = '';

    if (this.required && this.value == '') {
      this.isValid = false;
      this.validationMsg = 'required';
    } else if (!this.required && this.value == '') {
      this.isValid = true;
    }

    if (!this.isValid) {
      this._handle_validateFailed();

      if (this.validationMsg == '') {
        this.validationMsg = 'invalid';
      }
    } else {
      this._handle_validatePassed();
    }

    return this.isValid;
  }

  _handle_onFocus(event: FocusEvent) {
    this.activeTyping = true;
  }

  _handle_onBlur(event: FocusEvent) {
    this.activeTyping = false;
    if (this.selectedOption) {
      let sel = this.options.find((o) => o.id === this.selectedOption);
      if (sel) {
        this.sQuery = sel.name;
        this.boundValue = sel.id;
      } else {
        this.sQuery = '';
        this.boundValue = undefined;
      }
    }
  }

  _handle_onKeyDown(event: KeyboardEvent) {
    this.activeTyping = true;
    this.selectedOption = undefined;
    this.isSelected = false;
  }

  _handle_onKeyUp(event: KeyboardEvent) {
    if (this.sQuery.length > 0) {
      if (this.optionsFunction) {
        this.optionsFunction(this.sQuery.trim()).then((options: ListItem[]) => {
          this.options = options;
          this.optionsChanged = true;

          //this.options = this.records.filter(r => r.name.toLowerCase().includes(this.boundValue.toLowerCase()));
          //this.optionsChanged = true;
          if (this.options.length > 0) {
            //this.selectedOption = this.options[0].id;

            setTimeout(() => {
              this.openDropdown();
            });
          }

          if (event.key === 'Enter') {
            this.selectedOption = this.options[0].id;
            this.sQuery = this.options[0].name;
            this.boundValue = this.options[0].id;
            this.isSelected = true;
          }
        });
      }
    } else {
      this.options = [];
      this.selectedOption = undefined;
      this.isSelected = false;
      this.boundValue = undefined;
    }
  }

  ngAfterViewChecked() {
    if (this.optionsChanged) {
      this.openDropdown();
      this.optionsChanged = false; // Reset flag after opening
    }
  }

  openDropdown() {
    setTimeout(() => {
      if (this.selectElement) {
        this.selectElement.nativeElement.click();
        //this.selectElement.nativeElement.focus(); // Focus first
        this.selectElement.nativeElement.size = this.options.length; // Expand dropdown
        this.selectElement.nativeElement.selectedIndex = 0;
      }
    }, 0);
  }

  _handle_onChange(event: Event) {
    const selectedId = (event.target as HTMLSelectElement).value;
    const selectedItem = this.options.find((item) => item.id === selectedId);

    if (selectedItem) {
      this.selectedOption = selectedItem.id;
      this.sQuery = selectedItem.name;
      this.boundValue = selectedItem.id;
      this.actualValue = selectedItem.id;
      this.isSelected = true;

      // Call the onChange callback
      this.onChange(selectedItem.id);
      this.onTouch();
    }
  }

  clear_click() {
    this.selectedOption = undefined;
    this.isSelected = false;
    this.sQuery = '';
    this.boundValue = undefined;
    this.options = [];

    // Call the onChange callback with null/undefined
    this.onChange(undefined);
    this.onTouch();
  }

  override ngOnInit() {
    super.ngOnInit();

    if (this.boundValue) {
      // If we have an initial value, we should populate the query field
      if (this.optionsFunction) {
        this.optionsFunction(this.boundValue).then((options: ListItem[]) => {
          const selectedItem = options.find((o) => o.id === this.boundValue);
          if (selectedItem) {
            this.sQuery = selectedItem.name;
            this.selectedOption = selectedItem.id;
            this.isSelected = true;
          }
        });
      }
    }
  }

  override writeValue(value: any): void {
    super.writeValue(value);

    if (value) {
      // When a value is written, we need to update the display
      if (this.optionsFunction) {
        this.optionsFunction(value).then((options: ListItem[]) => {
          const selectedItem = options.find((o) => o.id === value);
          if (selectedItem) {
            this.sQuery = selectedItem.name;
            this.selectedOption = selectedItem.id;
            this.isSelected = true;
          }
        });
      }
    } else {
      this.sQuery = '';
      this.selectedOption = undefined;
      this.isSelected = false;
    }
  }
}
